import { SearchInput } from '@/catalog-apps/components/component-library/shared/SearchInput'
import { DynamicIcon } from '@/components/icons/DynamicIcon'
import { iconOptions, iconV2Options } from '@/components/icons/utils/IconOptions'
import { ScrollArea } from '@/components/shadcn/scroll-area'
import { ToggleGroup, ToggleGroupItem } from '@/components/shadcn/toggle-group'
import { cn } from '@/lib/utils'
import { IconV2CopyToClipboardOutline } from '@icons/IconV2CopyToClipboardOutline'
import { IconV2CorrectOutline } from '@icons/IconV2CorrectOutline'
import { motion } from 'motion/react'
import { useMemo, useState } from 'react'
import { toast } from 'sonner'

function IconCard({ icon }: { icon: string }) {
  const [isCopied, setIsCopied] = useState(false)

  const handleCopy = () => {
    navigator.clipboard.writeText(icon).then(() => {
      setIsCopied(true)
      toast.success(`Copied icon '${icon}' to clipboard`)
      setTimeout(() => setIsCopied(false), 3000)
    })
  }

  return (
    <motion.div
      layout
      className="group relative flex cursor-copy items-center gap-5 rounded-lg p-5 hover:bg-variant-hover hover:text-background"
      role="button"
      tabIndex={0}
      onClick={handleCopy}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          handleCopy()
        }
      }}
    >
      <DynamicIcon size={64} iconName={icon} lazyLoad />
      <span className="text-sm font-medium">{icon}</span>
      <div className="absolute right-3 top-3 ml-auto hidden group-hover:block">
        {isCopied ? <IconV2CorrectOutline size={24} /> : <IconV2CopyToClipboardOutline size={24} />}
      </div>
    </motion.div>
  )
}

export function IconsSection() {
  const [searchTerm, setSearchTerm] = useState('')
  const [iconVersion, setIconVersion] = useState<'v1' | 'v2'>('v2')

  const filteredIcons = useMemo(() => {
    const source = iconVersion === 'v1' ? iconOptions : iconV2Options
    const query = searchTerm.trim().toLowerCase()
    if (!query) {
      return source
    }

    return source.filter((icon) => icon.toLowerCase().includes(query))
  }, [searchTerm, iconVersion])

  return (
    <div className="flex flex-col">
      <div className="flex flex-wrap items-center justify-end gap-2 border-b px-6 py-4">
        <ToggleGroup
          className={cn('h-[35px] rounded-md border bg-background p-0.5')}
          type="single"
          value={iconVersion}
          onValueChange={(value) => {
            if (value) setIconVersion(value as 'v1' | 'v2')
          }}
        >
          <ToggleGroupItem className="h-[28px] px-2 text-xs font-medium" value="v1">
            V1
          </ToggleGroupItem>
          <ToggleGroupItem className="h-[28px] px-2 text-xs font-medium" value="v2">
            V2
          </ToggleGroupItem>
        </ToggleGroup>
        <SearchInput value={searchTerm} onChange={setSearchTerm} />
      </div>
      <ScrollArea className="h-[min(60vh,640px)]">
        <div className="grid w-full grid-cols-[repeat(auto-fill,minmax(350px,1fr))] px-3">
          {filteredIcons.map((icon) => (
            <IconCard key={icon} icon={icon} />
          ))}
        </div>
      </ScrollArea>
    </div>
  )
}
